/**
 * Chinese Translations (中文 - Simplified)
 */

import type { Translations } from '../types';

export const zh: Translations = {
  language: 'zh',
  languageName: '中文',

  common: {
    demo: '演示',
    privacy: '隐私',
    terms: '条款',
  },

  privacy: {
    title: '隐私政策',
    whatWeStore: '我们存储什么',
    whatWeStoreDesc:
      '我们临时处理面部几何数据用于年龄估算。验证完成后不会存储任何图像或生物特征模板。',
    whatWeDont: '我们不存储什么',
    whatWeDontDesc: '我们不存储照片、视频或生物特征标识符。您的面部数据在浏览器中处理后立即丢弃。',
    whyWeUseIt: '为什么使用',
    whyWeUseItDesc: '年龄估算帮助网站遵守年龄限制法规，无需要求个人文件或账户。',
    readFull: '阅读完整的',
    privacyPolicyLink: '隐私政策',
  },

  theme: {
    system: '系统',
    light: '浅色',
    dark: '深色',
  },

  face: {
    noFace: '未检测到面部',
    tooFar: '面部太远',
    tooClose: '面部太近',
    offCenter: '面部偏离中心',
    poorLighting: '光线不足',
    good: '分析中，请保持...',
    holdStill: '分析中，请保持...',
    holdStillCountdown: '保持不动... {count}',
    processing: '处理中...',
    looking: '未检测到面部',
    centerFace: '面部偏离中心',
    moveCloser: '面部太远',
    moveBack: '面部太近',
    keepInPosition: '保持位置',
    perfect: '完美',
    centerInFrame: '请将面部置于框内',
    outsideOval: '面部超出椭圆范围',
  },

  liveness: {
    blink: {
      title: '请眨眼',
      hint: '慢慢眨一次眼',
    },
    smile: {
      title: '请微笑',
      hint: '展示自然的微笑',
    },
    lookLeft: {
      title: '向左转',
      hint: '慢慢将头转向左边',
    },
    lookRight: {
      title: '向右转',
      hint: '慢慢将头转向右边',
    },
    openMouth: {
      title: '张嘴',
      hint: '请张大嘴巴',
    },
    lookUp: {
      title: '向上看',
      hint: '请抬起头',
    },
    lookDown: {
      title: '向下看',
      hint: '请低下头',
    },
    raiseBrows: {
      title: '抬起眉毛',
      hint: '请抬起您的眉毛',
    },
    improveLighting: {
      title: '改善光线',
      hint: '请移至光线更好的地方继续',
    },
  },

  document: {
    selectType: '选择您的证件类型',
    captureFront: '拍摄证件正面',
    captureBack: '拍摄证件背面',
    processing: '正在处理您的证件...',
    tips: {
      goodLighting: '确保光线充足',
      flatSurface: '将证件放在平坦的表面上',
      allCorners: '确保所有角落都可见',
      noGlare: '避免反光和阴影',
    },
    types: {
      passport: '护照',
      idCard: '身份证',
      driversLicense: '驾驶执照',
    },
  },

  ui: {
    continue: '继续',
    next: '下一步',
    retry: '重试',
    startOver: '重新开始',
    cancel: '取消',
    success: '年龄检查通过',
    failure: '年龄检查失败',
    loading: '加载中...',
    error: '发生错误',
    permissionRequired: '需要权限',
    cameraPermission: '需要摄像头权限',
    goBack: '返回',
    switchCamera: '切换摄像头',
    completeVerification: '完成',
    continueToId: '继续进行证件检查',
  },

  errors: {
    cameraNotFound: '此设备上未找到摄像头',
    cameraPermissionDenied: '摄像头权限被拒绝',
    cameraInUse: '摄像头正被其他应用程序使用',
    cameraNotSupported: '摄像头不支持所需设置',
    cameraSecurityError: '摄像头需要HTTPS连接',
    networkError: '网络错误。请检查您的连接。',
    timeout: '请求超时。请重试。',
    invalidApiKey: 'API密钥无效',
    sessionExpired: '会话已过期。请重新开始。',
    verificationFailed: '检查失败。请重试。',
    unknownError: '发生意外错误',
    cameraAccessDeniedDetailed: '请允许摄像头访问以继续。',
    cameraNotFoundDetailed: '未检测到摄像头。请连接摄像头后重试。',
    cameraInUseDetailed: '摄像头正被其他应用程序使用。请关闭该应用后重试。',
    cameraOverconstrained: '摄像头不支持所请求的分辨率。正在使用默认设置尝试...',
    networkTimeout: '请求超时。请检查您的网络连接后重试。',
    networkOffline: '您似乎处于离线状态。请检查您的网络连接。',
    apiGenericError: '出现了问题。请重试。',
    faceNotDetected: '请将脸部置于框内。',
    livenessFailed: '活体检测失败。请重试。',
    documentCaptureFailed: '无法拍摄证件。请确保光线充足后重试。',
    initializationFailed: '加载失败。请刷新页面重试。',
    modelLoadFailed: '必要组件加载失败。请刷新页面后重试。',
    unexpectedError: '发生意外错误。请重试。',
    configError: '配置错误。请联系客服。',
  },

  screens: {
    welcome: {
      title: '18+ 年龄验证',
      subtitle: '匿名年龄验证',
      startButton: '开始',
      secureNote: '安全加密',
      ageTechBy: '年龄技术由 AppBouncer 提供',
      waitingCamera: '等待摄像头访问',
      allowCamera: '允许摄像头访问',
      noBiometric: '不存储生物识别数据',
      loading: '加载中...',
      loadingRequirements: '加载要求中...',
    },
    permissions: {
      title: '加载中 ...',
      description: '请在提示时允许访问摄像头',
      allowButton: '允许摄像头访问',
      privacyNote: '您的数据已加密并受保护',
      goodLighting: '良好光线！',
      goodLightingHint: '确保您的脸部光线充足',
      getReady: '做好准备',
      removeCoverings: '摘下口罩',
      removeGlasses: '摘下眼镜',
    },
    facePosition: {
      title: '调整面部位置',
      hint: '居中面部',
      step: '步骤',
      stepOf: '步骤 {current}/{total}',
      lookingForFace: '正在寻找脸部...',
    },
    livenessCheck: {
      title: '活体检测',
      followInstructions: '请按照指示操作',
      preparing: '准备中...',
      complete: '活体检测通过！',
      actionAutoDetected: '动作将被自动检测',
      failed: '活体检测失败',
      failedDescription:
        '我们无法验证您的活体状态。请确保光线充足，直视摄像头，并按照屏幕上的指示操作。',
      retryButton: '重试活体检测',
      restartButton: '重新开始',
    },
    ageEstimation: {
      title: '请保持不动',
      analyzing: '分析中...',
      analyzingProgress: '正在分析第 {current} 张脸部（共 {total} 张）...',
      estimatedAge: '估计年龄',
      holdStill: '请保持不动',
      verified: '年龄已验证',
      verificationUncertain: '年龄不确定',
      estimationComplete: '年龄检查完成',
      confirmedAge: '已确认 {minAge}+ 岁',
      couldNotConfirm: '无法确认 {minAge}+ 岁',
      ageConfirmed: '年龄已确认',
      aboveMinimumAge: '您符合最低年龄要求',
      belowMinimumAge: '低于最低年龄',
      documentRequired: '需要证件',
      uploadDocumentMessage: '请上传有效身份证件以验证您的年龄',
      idHint: '身份证、护照或驾驶证',
      confidenceLevels: '各年龄阈值置信度',
      confidence: '置信度',
      years: '岁',
      continueToId: '继续进行证件检查',
      demoComplete: '演示模式完成。在生产环境中，您将被重定向。',
      improveLighting: '改善光线',
      improveLightingHint: '请移至光线更好的地方以获得准确结果',
    },
    documentSelect: {
      title: '选择证件类型',
      description: '选择您要扫描的身份证件类型',
      idCard: '国民身份证',
      idCardHint: '政府颁发的身份证',
      passport: '护照',
      passportHint: '仅限照片页',
      driversLicense: '驾驶执照',
      driversLicenseHint: '有效的驾驶执照',
    },
    captureMode: {
      captureFrontSide: '拍摄正面',
      captureBackSide: '拍摄背面',
      chooseMethod: '选择证件拍摄方式',
      useCamera: '使用摄像头',
      useCameraHint: '拍摄证件照片',
      uploadFile: '上传文件',
      uploadFileHint: '从设备中选择',
    },
    documentCamera: {
      frontSide: '正面',
      backSide: '背面',
      positionInFrame: '将证件放入框架内',
      scanFront: '扫描证件正面',
      scanBack: '扫描证件背面',
      keepFlat: '保持证件平整并确保光线充足',
      capturePhoto: '拍照',
    },
    documentPreview: {
      frontPreview: '正面预览',
      backPreview: '背面预览',
      lookingGood: '看起来不错！',
      checkDetails: '请确保所有细节清晰可见',
      imageQuality: '图片质量',
      qualityGood: '良好',
      usePhoto: '使用此照片',
      retakePhoto: '重新拍摄',
    },
    documentCapture: {
      captureFront: '拍摄正面',
      captureBack: '拍摄背面',
      alignDocument: '将证件对齐在框架内',
      captureButton: '拍摄',
      retakeButton: '重拍',
      confirmButton: '确认',
      frontSide: '正面',
      backSide: '背面',
      positionDocument: '将证件放入框架内',
      alignWithFrame: '将证件与框架对齐',
      positionFrontId: '放置身份证正面',
      positionBackId: '放置身份证背面',
      scanFrontId: '扫描身份证正面',
      scanBackId: '扫描身份证背面',
      positionWithinFrame: '将正面放入框架内',
      flipAndScan: '翻转身份证扫描背面',
      goodLight: '光线充足',
      flatSurface: '平整表面',
      frontCaptured: '正面已拍摄',
      captureIdCard: '拍摄身份证',
      capturePassport: '拍摄护照',
      captureDriversLicense: '拍摄驾驶执照',
    },
    processing: {
      title: '处理中',
      description: '请稍候，正在处理',
      analyzingDocument: '正在分析证件...',
      almostDone: '即将完成...',
      faceVerified: '面部已验证',
      livenessConfirmed: '活体已确认',
      processingDocuments: '正在处理证件...',
    },
    success: {
      title: '年龄验证通过',
      description: '年龄和活体检查完成',
      redirecting: '正在跳转...',
      summary: '摘要',
      faceMatch: '面部匹配',
      liveness: '活体检测',
      age: '年龄',
      idDocument: '身份证件',
      pass: '通过',
      fail: '未通过',
      valid: '有效',
      notApplicable: '不适用',
      done: '完成',
      feedbackTitle: '请评价您的体验',
      feedbackPlaceholder: '告诉我们如何改进...',
      skip: '跳过',
      submit: '提交',
    },
    failure: {
      title: '年龄检查失败',
      description: '无法完成检查。请重试。',
      tryAgainButton: '重试',
      leaveButton: '离开',
      attemptsLeft: '剩余 {{count}} 次尝试',
      noAttemptsLeft: '没有更多尝试机会',
    },
    error: {
      title: '需要配置',
      description: 'URL中缺少一些必需参数。请联系网站管理员。',
      missingParams: '缺少参数',
      requiredParams: '必需参数',
      technicalDetails: '技术详情',
      urlMustInclude: 'URL必须包含以下查询参数:',
    },
    underage: {
      title: '未达到年龄要求',
      description: '您必须年满{{minAge}}岁才能访问此服务。',
    },
    consent: {
      title: '帮助改进年龄检查',
      description:
        '允许AppBouncer收集少量匿名使用数据（如摄像头捕获）以改进年龄验证和后备验证。您的数据永远不会被出售或提供给第三方。',
      allowButton: '允许',
    },
    documentConsent: {
      title: '需要文件',
      description: '为完成检查，我们需要使用官方文件验证您的年龄。您是否同意上传您的身份证件？',
      allowButton: '继续上传文件',
      declineButton: '拒绝',
      privacyNote: '您的文件将被安全处理并在检查后删除。',
    },
    selfDeclaration: {
      title: '您的出生日期',
      description: '请输入您的出生日期以继续',
      day: '日',
      month: '月',
      year: '年',
      yourAge: '您的年龄：',
      years: '岁',
      confirmButton: '继续',
      months: {
        january: '一月',
        february: '二月',
        march: '三月',
        april: '四月',
        may: '五月',
        june: '六月',
        july: '七月',
        august: '八月',
        september: '九月',
        october: '十月',
        november: '十一月',
        december: '十二月',
      },
    },
  },

  lighting: {
    warning: '光线问题',
    adjust: '调整光线',
    lowLight: '检测到光线不足',
    tooBright: '光线过亮',
    poorContrast: '对比度不足',
    improveLighting: '寻找更好的光线',
    moveToLighter: '移至光线更好的地方',
    videoNotReady: '视频未就绪',
  },

  steps: {
    faceDetection: '人脸检测',
    liveness: '活体检测',
    age: '年龄检查',
    documentScan: '证件扫描',
    selfDeclaration: '自我声明',
  },

  status: {
    cameraStarted: '摄像头已启动',
    cameraReady: '摄像头就绪',
    cameraAccessDenied: '摄像头访问被拒绝',
    cameraError: '摄像头错误',
    cameraNotAvailable: '摄像头不可用',
    noFaceDetected: '未检测到面部',
    livenessVerified: '活体验证完成',
    livenessCheckFailed: '活体检测失败',
    verifyingAge: '正在验证年龄...',
    processingDocument: '正在处理证件...',
    documentProcessed: '证件处理成功',
    verificationCancelled: '检查已取消',
    improveLightingForAge: '改善光线以获得准确结果',
    getReady: '准备好',
    preparingLiveness: '正在准备活体检测...',
    uploadingSelfie: '正在上传自拍照片...',
    preparingUpload: '正在准备上传...',
    uploadingFront: '正在上传正面证件...',
    uploadingBack: '正在上传背面证件...',
    processingDocuments: '正在处理证件...',
    documentSubmissionFailed: '证件提交失败。请重试。',
    selectJpegOrPng: '请选择JPEG或PNG格式的图片',
    fileTooLarge: '文件过大。最大允许5MB。',
    errorReadingFile: '读取文件出错',
    frontDocumentRequired: '需要正面证件图片',
    verificationFailedClose: '检查失败。您将被重定向。',
    verificationCompleteClose: '检查完成！您将被重定向。',
    analyzingFace: '正在分析面部...',
  },
};
